/**
 * Pure data-shaping helpers for the dashboard's "Today's sales" charts
 * (BarChart/PieChart - see ./BarChart.tsx, ./PieChart.tsx). Kept free of any
 * DOM/SVG so they're unit-testable on their own.
 *
 * Colour: see the long comment above `--chart-1`..`--chart-other` in
 * `web/src/theme/tokens.css` for how this exact 7-slot palette was validated
 * (the data-viz skill's validate_palette.js - OKLCH lightness band, chroma
 * floor, CVD separation on both the adjacent and all-pairs pairlists,
 * contrast vs. the white card surface). The short version:
 *   - The bar chart's stacked segments only ever need to differ from their
 *     immediate neighbours in the stack ("adjacent" pairlist) - all 6 hue
 *     slots clear that with zero WARN, so up to 6 distinct groups get their
 *     own colour there.
 *   - The pie/donut's slices can all sit next to each other ("all-pairs"
 *     pairlist, the stricter test) - only the first 4 hue slots clear that,
 *     so the pie caps itself at 4 distinct colours (`PIE_MAX_SLICES`) and
 *     folds anything past the 4th slot into "Other", regardless of that
 *     slice's own share of the total.
 *   - Both charts assign colour by RANK in the same `totals` order (largest
 *     qty first, as the API already returns it), so a given category/
 *     product keeps the same colour whichever chart it appears in.
 */

/** Fixed hue order - see tokens.css. Never cycled; a group beyond the last
 * slot folds into `OTHER_COLOR` instead of repeating a hue. */
export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
] as const;

/** Deliberately achromatic - the "Other" bucket, never an 8th hue. */
export const OTHER_COLOR = 'var(--chart-other)';

/** The pie/donut's distinct-colour cap (see the module comment). */
export const PIE_MAX_SLICES = 4;

/** A slice below this share of the pie's total folds into "Other". */
export const PIE_OTHER_THRESHOLD = 0.03;

export interface KeyedTotal {
  key: string;
  label: string;
  qty: number;
}

/** The colour for the Nth-ranked group (0 = largest), shared by both charts. */
export function colorForRank(rank: number): string {
  return rank < CHART_COLORS.length ? CHART_COLORS[rank]! : OTHER_COLOR;
}

/** Maps every group's `key` to its colour, by its rank in `totals` (already
 * sorted largest-first by the API). Used by the bar chart, which can show up
 * to `CHART_COLORS.length` distinct colours before folding into "Other". */
export function buildColorMap(totals: readonly KeyedTotal[]): Map<string, string> {
  return new Map(totals.map((t, i) => [t.key, colorForRank(i)]));
}

/**
 * Largest-remainder rounding: integer percentages that always sum to exactly
 * 100 (for a non-empty, non-zero input), instead of naive per-value rounding
 * which can land on 99 or 101.
 */
export function roundPercentagesTo100(values: readonly number[]): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (values.length === 0 || total <= 0) return values.map(() => 0);

  const raw = values.map((v) => (v / total) * 100);
  const floors = raw.map((v) => Math.floor(v));
  const remainder = 100 - floors.reduce((sum, v) => sum + v, 0);

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  const result = [...floors];
  for (let k = 0; k < remainder; k += 1) {
    result[order[k]!.i]! += 1;
  }
  return result;
}

export interface PieSlice {
  key: string;
  label: string;
  qty: number;
  /** Rounded percentage of the pie's total; every slice's `pct` sums to 100. */
  pct: number;
  color: string;
  isOther: boolean;
}

/**
 * Orders `totals` largest-first (already the API's order, but defensive),
 * keeps at most `PIE_MAX_SLICES` groups whose individual share clears
 * `PIE_OTHER_THRESHOLD`, and folds everything else into one trailing
 * "Other" slice. Returns `[]` for an empty/all-zero input.
 */
export function buildPieSlices(totals: readonly KeyedTotal[]): PieSlice[] {
  const total = totals.reduce((sum, t) => sum + t.qty, 0);
  if (total <= 0) return [];

  const kept: KeyedTotal[] = [];
  let otherQty = 0;
  totals.forEach((t, i) => {
    const share = t.qty / total;
    if (i < PIE_MAX_SLICES && share >= PIE_OTHER_THRESHOLD) {
      kept.push(t);
    } else {
      otherQty += t.qty;
    }
  });

  const qtys = kept.map((t) => t.qty).concat(otherQty > 0 ? [otherQty] : []);
  const pcts = roundPercentagesTo100(qtys);

  const slices: PieSlice[] = kept.map((t, i) => ({
    key: t.key,
    label: t.label,
    qty: t.qty,
    pct: pcts[i]!,
    color: colorForRank(i),
    isOther: false,
  }));
  if (otherQty > 0) {
    slices.push({
      key: 'other',
      label: 'Other',
      qty: otherQty,
      pct: pcts[pcts.length - 1]!,
      color: OTHER_COLOR,
      isOther: true,
    });
  }
  return slices;
}

/** "9am", "12pm", "11pm" - 12-hour, lowercase am/pm, no colon (whole hours only). */
export function formatHour12(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  const period = h < 12 ? 'am' : 'pm';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${period}`;
}

function niceNumber(value: number, round: boolean): number {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction: number;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

/**
 * Clean, evenly-spaced Y-axis ticks from 0 up to (at least) `maxValue`,
 * e.g. [0, 5, 10, 15] - a standard "nice numbers" tick algorithm, not a
 * fixed division of the raw max. `maxValue <= 0` yields just `[0]`.
 */
export function computeYAxisTicks(maxValue: number, desiredTicks = 4): number[] {
  if (maxValue <= 0) return [0];
  const roughRange = niceNumber(maxValue, false);
  const step = niceNumber(roughRange / Math.max(1, desiredTicks - 1), true);
  const niceMax = Math.ceil(maxValue / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step / 1000; v += step) {
    ticks.push(Math.round(v));
  }
  return ticks;
}
